import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { PlannedMove } from '@nyos/shared'

/**
 * Drag-to-reorder list for planned cut moves (ADR: @dnd-kit; Konva is
 * render-only). Reordering changes pass priority — the crew's execution
 * order. Pure UI state; the move stack itself is owned by the page.
 */

export interface BuilderMove extends PlannedMove {
  /** Stable client-side id — dnd-kit needs a key that survives reordering. */
  clientId: string
}

interface SortableMoveListProps {
  moves: BuilderMove[]
  trackName: (trackId: string | undefined) => string
  onReorder: (from: number, to: number) => void
  onRemove: (index: number) => void
}

export function SortableMoveList({ moves, trackName, onReorder, onRemove }: SortableMoveListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }
    const from = moves.findIndex((m) => m.clientId === active.id)
    const to = moves.findIndex((m) => m.clientId === over.id)
    if (from >= 0 && to >= 0) {
      onReorder(from, to)
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={moves.map((m) => m.clientId)} strategy={verticalListSortingStrategy}>
        <div className="rounded-md border border-border divide-y divide-border">
          {moves.map((move, i) => (
            <SortableMoveRow
              key={move.clientId}
              move={move}
              index={i}
              trackName={trackName}
              onRemove={() => onRemove(i)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

function SortableMoveRow({
  move,
  index,
  trackName,
  onRemove,
}: {
  move: BuilderMove
  index: number
  trackName: (trackId: string | undefined) => string
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: move.clientId })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
      }}
      className="px-3 py-2 text-sm flex items-center gap-3 bg-background"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground hover:text-foreground touch-none px-1"
        title="Drag to reorder"
        aria-label={`Reorder move ${index + 1}`}
      >
        ⠿
      </button>
      <span className="text-sm text-muted-foreground w-6">#{move.priority ?? index + 1}</span>
      <span className="flex-1">
        {move.carIds.length} car(s): {trackName(move.sourceTrackId)}
        {' → '}
        {trackName(move.targetTrackId)}
        {move.targetDoorNumber ? ` · Door ${move.targetDoorNumber}` : ''}
      </span>
      <button onClick={onRemove} className="text-sm text-destructive hover:underline">
        remove
      </button>
    </div>
  )
}
